import React, { useState, useEffect, useCallback } from 'react';
import MessageList from './MessageList';
import MessageDetail from './MessageDetail';
import ComposeMessageModal from './ComposeMessageModal';
import { useAuth } from '../../context/AuthContext';
import { fetchMailboxData, fetchMessagesForThread } from '../../services/messagingService';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Fade,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Outbox as OutboxIcon,
  Send as SendIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const folders = [
  { key: 'inbox', label: 'Inbox', icon: InboxIcon },
  { key: 'outbox', label: 'Drafts', icon: OutboxIcon },
  { key: 'sent', label: 'Sent', icon: SendIcon },
  { key: 'archived', label: 'Archived', icon: ArchiveIcon },
  { key: 'trash', label: 'Trash', icon: DeleteIcon },
];

const MailboxLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, loading: authLoading } = useAuth();
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selectedThread, setSelectedThread] = useState(null);
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardFrom, setForwardFrom] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadMailboxData = useCallback(async () => {
    if (!user) return;
    setRefreshing(true);
    setLoadingThreads(true);
    try {
      const response = await fetchMailboxData(user.uid);
      if (response && response.mailbox && Array.isArray(response.mailbox)) {
        setThreads(response.mailbox);
      } else {
        console.warn('Unexpected mailbox data format:', response);
        setThreads([]);
      }
    } catch (error) {
      console.error('Error fetching mailbox data:', error);
      setThreads([]);
    } finally {
      setLoadingThreads(false);
      setRefreshing(false);
    }
  }, [user]);

  // Fetch threads for user
  useEffect(() => {
    loadMailboxData();
  }, [loadMailboxData]);

  // Clear selected thread when folder changes
  useEffect(() => {
    setSelectedThread(null);
  }, [selectedFolder]);

  // Fetch messages for selected thread
  useEffect(() => {
    if (!selectedThread) {
      setMessages([]);
      return;
    }
    
    // If it's a draft, create a mock message from the draft data
    if (selectedThread.isDraft) {
      const draftMessage = {
        id: selectedThread.id,
        content: selectedThread.message || '',
        sentAt: selectedThread.updatedAt || selectedThread.createdAt,
        sentBy: selectedThread.senderId,
        senderName: selectedThread.senderName,
        priority: selectedThread.priority,
        type: 'draft',
        subject: selectedThread.subject,
        recipients: selectedThread.recipients || []
      };
      setMessages([draftMessage]);
      return;
    }
    
    // Otherwise, fetch messages for the thread
    setLoadingMessages(true);
    fetchMessagesForThread(selectedThread.id)
      .then(response => {
        if (response && response.messages && Array.isArray(response.messages)) {
          setMessages(response.messages);
        } else {
          console.warn('Unexpected messages data format:', response);
          setMessages([]);
        }
      })
      .catch(error => {
        console.error('Error fetching messages:', error);
        setMessages([]);
      })
      .finally(() => setLoadingMessages(false));
  }, [selectedThread]);

  // Filter threads by folder and search
  const filteredThreads = threads.filter(t => {
    const searchMatch = !search || 
      (t.subject?.toLowerCase().includes(search.toLowerCase()) ||
       t.lastMessage?.toLowerCase().includes(search.toLowerCase()));

    if (!searchMatch) return false;

    switch (selectedFolder) {
      case 'inbox':
        return !t.isArchived && !t.isTrashed && !t.isDraft;
      case 'outbox':
        return t.isDraft;
      case 'sent':
        return !t.isDraft && t.createdBy === user?.uid && !t.isArchived && !t.isTrashed;
      case 'archived':
        return t.isArchived && !t.isTrashed && !t.isDraft;
      case 'trash':
        return t.isTrashed && !t.isDraft;
      default:
        return true;
    }
  });

  // Count threads per folder for badges
  const getCountForFolder = (folderKey) => {
    return threads.filter(t => {
      switch (folderKey) {
        case 'inbox':
          return !t.isArchived && !t.isTrashed && !t.isDraft;
        case 'outbox':
          return t.isDraft;
        case 'sent':
          return !t.isDraft && t.createdBy === user?.uid && !t.isArchived && !t.isTrashed;
        case 'archived':
          return t.isArchived && !t.isTrashed && !t.isDraft;
        case 'trash':
          return t.isTrashed && !t.isDraft;
        default:
          return false;
      }
    }).length;
  };

  // Handle reply action
  const handleReply = (message) => {
    setReplyTo(message);
    setForwardFrom(null);
    setIsComposeOpen(true);
  };

  // Handle forward action
  const handleForward = (message) => {
    setForwardFrom(message);
    setReplyTo(null);
    setIsComposeOpen(true);
  };

  // Handle edit draft action
  const handleEditDraft = (draft) => {
    setReplyTo(draft);
    setForwardFrom(null);
    setIsComposeOpen(true);
  };

  // Handle compose modal close
  const handleComposeClose = () => {
    setIsComposeOpen(false);
    setReplyTo(null);
    setForwardFrom(null);
    loadMailboxData(); // Refresh after compose
  };

  // Handle back button on mobile
  const handleBackToList = () => {
    setSelectedThread(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedFolder(newValue);
  };

  // Show detail view on mobile when thread is selected
  const showDetailOnMobile = isMobile && selectedThread;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      minHeight: 'calc(100vh - 64px)',
      bgcolor: '#f5f7fa',
    }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 0,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Title Row */}
        <Box sx={{ 
          px: { xs: 2, sm: 3, md: 4 }, 
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: 'white',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            Secure Messaging
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={loadMailboxData}
                disabled={refreshing}
                sx={{ 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsComposeOpen(true)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 2, sm: 3 },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                boxShadow: 'none',
              }}
            >
              {isMobile ? 'New' : 'Compose'}
            </Button>
          </Box>
        </Box>

        {/* Tabs Row */}
        <Box sx={{ 
          px: { xs: 0, sm: 2, md: 3 },
          bgcolor: 'rgba(0,0,0,0.1)',
        }}>
          <Tabs
            value={selectedFolder}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              '& .MuiTabs-indicator': {
                bgcolor: 'white',
                height: 3,
              },
              '& .MuiTabs-scrollButtons': {
                color: 'white',
              },
            }}
          >
            {folders.map((folder) => {
              const IconComponent = folder.icon;
              const count = getCountForFolder(folder.key);
              return (
                <Tab
                  key={folder.key}
                  value={folder.key}
                  icon={
                    <Badge 
                      badgeContent={count > 0 ? count : null} 
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: 16,
                          height: 16,
                        }
                      }}
                    >
                      <IconComponent sx={{ fontSize: 20 }} />
                    </Badge>
                  }
                  iconPosition="start"
                  label={!isMobile ? folder.label : undefined}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    minHeight: 48,
                    minWidth: isMobile ? 60 : 100,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&.Mui-selected': {
                      color: 'white',
                      fontWeight: 600,
                    },
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Paper>

      {/* Search Bar */}
      <Box sx={{ 
        px: { xs: 2, sm: 3, md: 4 }, 
        py: 2,
        bgcolor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search messages or contacts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#eef1f5',
              },
              '&.Mui-focused': {
                bgcolor: 'white',
              },
            },
          }}
        />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1,
        overflow: 'hidden',
        minHeight: 0,
      }}>
        {/* Message List Panel */}
        <Fade in={!showDetailOnMobile}>
          <Box sx={{ 
            width: { xs: '100%', md: 400, lg: 420 },
            flexShrink: 0,
            display: showDetailOnMobile ? 'none' : 'flex',
            flexDirection: 'column',
            borderRight: { md: '1px solid' },
            borderColor: 'divider',
            bgcolor: 'white',
            overflow: 'hidden',
          }}>
            <MessageList
              folder={selectedFolder}
              threads={filteredThreads}
              onSelectThread={setSelectedThread}
              selectedThread={selectedThread}
              search={search}
              loading={loadingThreads || authLoading}
            />
          </Box>
        </Fade>

        {/* Message Detail Panel */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fafbfc',
          overflow: 'hidden',
          minWidth: 0,
        }}>
          {isMobile && selectedThread && (
            <Box sx={{ 
              p: 1, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white',
            }}>
              <IconButton onClick={handleBackToList}>
                <ArrowBackIcon />
              </IconButton>
            </Box>
          )}
          <MessageDetail
            thread={selectedThread}
            messages={messages}
            loading={loadingMessages}
            onThreadDeleted={() => setSelectedThread(null)}
            onReply={handleReply}
            onForward={handleForward}
            onEditDraft={handleEditDraft}
          />
        </Box>
      </Box>

      {/* Compose Modal */}
      {isComposeOpen && (
        <ComposeMessageModal 
          onClose={handleComposeClose} 
          replyTo={replyTo}
          forwardFrom={forwardFrom}
        />
      )}
    </Box>
  );
};

export default MailboxLayout;
